import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import CircularProgress from '../../common/CircularProgress';


type Props = {
  open: boolean;
  onClose: () => void;
  date: string;
  startTime: string;
  endTime: string;
  loading?: boolean;
  existingSchedules?: { startTime: string; endTime: string; id: string | number }[];
  allSchedules?: { startTime: string; endTime: string; id: string | number; weekday: number }[];
  onSave: (inputs: Array<{ startTime: string; endTime: string; weekdayIdx: number; scheduleType: 'standard' | 'reschedule_only' }>) => Promise<void> | void;
};

function timeToMinutes(hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

function hasOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
  const s1 = timeToMinutes(start1);
  let e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  let e2 = timeToMinutes(end2);
  
  if (e1 === 0) e1 = 24 * 60;
  if (e2 === 0) e2 = 24 * 60;
  
  return s1 < e2 && e1 > s2;
}

const TIMES_30: string[] = Array.from({ length: 48 }).map((_, i) => {
  const hh = String(Math.floor(i / 2)).padStart(2, '0');
  const mm = i % 2 === 0 ? '00' : '30';
  return `${hh}:${mm}`;
});

// Opciones de inicio: excluir horarios donde no cabe al menos 1h antes de medianoche (23:30+)
const START_TIMES_30: string[] = TIMES_30.filter(t => {
  const m = parseInt(t.split(':')[0], 10) * 60 + parseInt(t.split(':')[1], 10);
  return (24 * 60 - m) >= 60;
});

type TimeSelectProps = {
  value: string;
  onChange: (v: string) => void;
  ariaLabel?: string;
  options?: string[];
};

const TimeSelect: React.FC<TimeSelectProps> = ({ value, onChange, ariaLabel, options }) => {
  const items = options || TIMES_30;
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number; width: number; maxH: number }>({ top: 0, left: 0, width: 0, maxH: 260 });

  useEffect(() => {
    const getScrollableAncestor = (el: HTMLElement | null): HTMLElement | null => {
      let node: HTMLElement | null = el;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const canScroll = /(auto|scroll)/i.test(style.overflowY || '') || node.scrollHeight > node.clientHeight;
        if (canScroll) return node;
        node = node.parentElement;
      }
      return document.getElementById('create-work-range-modal-content') as HTMLElement | null;
    };

    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    // Cuando se abre, desplazar el modal para que el dropdown quede completamente visible
    if (open && containerRef.current) {
      const scrollable = getScrollableAncestor(containerRef.current);
      const DROPDOWN_H = 260;
      if (scrollable) {
        const contRect = containerRef.current.getBoundingClientRect();
        const parentRect = scrollable.getBoundingClientRect();
        const overflowPx = contRect.bottom + DROPDOWN_H - parentRect.bottom;
        if (overflowPx > 0) {
          scrollable.scrollTop += overflowPx + 8;
        }
      }
    }
    // Recalcular posición del menú para que siempre quede dentro del viewport
    const updatePos = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom - 12;
      const maxH = Math.max(160, Math.min(320, spaceBelow));
      setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width, maxH });
    };
    updatePos();
    if (open) {
      window.addEventListener('resize', updatePos);
      window.addEventListener('scroll', updatePos, true);
      // Scroll al item seleccionado
      requestAnimationFrame(() => {
        const list = listRef.current;
        if (list) {
          const idx = items.indexOf(value);
          if (idx > 0) {
            const itemH = 36;
            list.scrollTop = Math.max(0, idx * itemH - menuPos.maxH / 2 + itemH / 2);
          }
        }
      });
    }
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open, items, value, menuPos.maxH]);

  const handleSelect = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const scrollList = (direction: 'up' | 'down') => {
    const list = listRef.current;
    if (!list) return;
    list.scrollBy({ top: direction === 'up' ? -108 : 108, behavior: 'smooth' });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen(o => !o)}
        ref={buttonRef}
        style={{
          width: '100%',
          padding: '0.625rem 0.75rem',
          fontSize: '0.875rem',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          background: '#fff',
          textAlign: 'left',
          cursor: 'pointer'
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = '#6D57BC')}
        onBlur={(e) => (e.currentTarget.style.borderColor = '#d1d5db')}
      >
        {value}
        <span style={{ float: 'right', opacity: 0.7 }}>▾</span>
      </button>
      {open && createPortal(
        <div
          style={{
            position: 'fixed',
            top: `${menuPos.top}px`,
            left: `${menuPos.left}px`,
            width: `${menuPos.width}px`,
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            maxHeight: menuPos.maxH,
            zIndex: 3000
          }}
        >
          {/* Botón subir */}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); scrollList('up'); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 28,
              flexShrink: 0,
              border: 'none',
              borderBottom: '1px solid #f3f4f6',
              background: '#fff',
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '0.75rem',
              borderRadius: '8px 8px 0 0',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
          >
            ▲
          </button>
          <style>{`.time-select-list::-webkit-scrollbar { width: 8px; } .time-select-list::-webkit-scrollbar-track { background: #f9fafb; } .time-select-list::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; } .time-select-list::-webkit-scrollbar-thumb:hover { background: #9ca3af; }`}</style>
          <div
            ref={listRef}
            role="listbox"
            className="time-select-list"
            style={{
              flex: 1,
              overflowY: 'scroll',
            }}
          >
            {items.map(t => (
              <div
                key={t}
                role="option"
                aria-selected={t === value}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(t); }}
                style={{
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                  background: t === value ? '#f3f4f6' : '#fff',
                  fontWeight: t === value ? 600 : 400,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#f9fafb'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = t === value ? '#f3f4f6' : '#fff'; }}
              >
                {t}
              </div>
            ))}
          </div>
          {/* Botón bajar */}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); scrollList('down'); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 28,
              flexShrink: 0,
              border: 'none',
              borderTop: '1px solid #f3f4f6',
              background: '#fff',
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '0.75rem',
              borderRadius: '0 0 8px 8px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
          >
            ▼
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

function getOccupiedIntervals(
  rowIndex: number,
  allRanges: Array<{ startTime: string; endTime: string }>,
  schedulesForDay: Array<{ startTime: string; endTime: string }>,
): Array<{ start: number; end: number }> {
  const occupied: Array<{ start: number; end: number }> = [];
  for (let i = 0; i < allRanges.length; i++) {
    if (i === rowIndex) continue;
    const r = allRanges[i];
    if (!r.startTime || !r.endTime) continue;
    const s = timeToMinutes(r.startTime);
    const e = timeToMinutes(r.endTime) === 0 ? 24 * 60 : timeToMinutes(r.endTime);
    occupied.push({ start: s, end: e });
  }
  for (const s of schedulesForDay) {
    const start = timeToMinutes(s.startTime);
    const end = timeToMinutes(s.endTime) === 0 ? 24 * 60 : timeToMinutes(s.endTime);
    occupied.push({ start, end });
  }
  return occupied;
}

function getFilteredStartOptions(
  occupied: Array<{ start: number; end: number }>,
  endTime?: string,
): string[] {
  const endMin = endTime ? (timeToMinutes(endTime) === 0 ? 24 * 60 : timeToMinutes(endTime)) : null;
  return START_TIMES_30.filter(t => {
    const m = timeToMinutes(t);
    // Excluir si es >= endTime o no deja al menos 1h antes de endTime
    if (endMin !== null && m >= endMin) return false;
    if (endMin !== null && endMin - m < 60) return false;
    // Excluir si cae dentro de un rango ocupado
    if (occupied.some(o => m >= o.start && m < o.end)) return false;
    // Excluir si no hay al menos 1h libre antes del próximo rango ocupado
    let nextStart = 24 * 60;
    for (const o of occupied) {
      if (o.start > m && o.start < nextStart) nextStart = o.start;
    }
    if (nextStart - m < 60) return false;
    return true;
  });
}

function getFilteredEndOptions(
  start: string,
  occupied: Array<{ start: number; end: number }>,
): string[] {
  if (!start) return TIMES_30;
  const startMin = timeToMinutes(start);
  const minEndMin = startMin + 60;

  // Cap end at the earliest occupied range that starts after our start
  let maxEndMin = 24 * 60;
  for (const o of occupied) {
    if (o.start > startMin && o.start < maxEndMin) {
      maxEndMin = o.start;
    }
  }

  const filtered = TIMES_30.filter(t => {
    const tMin = timeToMinutes(t);
    if (tMin === 0) return false;
    return tMin >= minEndMin && tMin <= maxEndMin;
  });
  // 00:00 = medianoche solo si cabe y no hay ocupado antes
  if (startMin >= 23 * 60 && (24 * 60 - startMin) >= 60 && maxEndMin === 24 * 60) {
    filtered.push('00:00');
  }
  return filtered;
}

function validateRange(
  range: { startTime: string; endTime: string },
  index: number,
  allRanges: Array<{ startTime: string; endTime: string }>,
  schedulesForDay: Array<{ startTime: string; endTime: string }>,
): string {
  const { startTime: st, endTime: et } = range;
  if (!st || !et) return '';
  const startMinRaw = timeToMinutes(st);
  const endMinRaw = timeToMinutes(et);
  if (startMinRaw % 30 !== 0 || endMinRaw % 30 !== 0) {
    return 'Los horarios deben estar en intervalos de 30 minutos';
  }
  const endMin = endMinRaw === 0 ? 24 * 60 : endMinRaw;
  if (endMin <= startMinRaw) {
    return 'La hora de fin debe ser posterior a la hora de inicio';
  }
  const overlapping = schedulesForDay.find(s => hasOverlap(st, et, s.startTime, s.endTime));
  if (overlapping) {
    return `Se solapa con horario existente: ${overlapping.startTime} - ${overlapping.endTime}`;
  }
  for (let j = 0; j < allRanges.length; j++) {
    if (j === index) continue;
    const other = allRanges[j];
    if (!other.startTime || !other.endTime) continue;
    if (hasOverlap(st, et, other.startTime, other.endTime)) {
      return `Se solapa con otro rango: ${other.startTime} - ${other.endTime}`;
    }
  }
  return '';
}

const CreateWorkRangeModal: React.FC<Props> = ({ open, onClose, date, startTime, endTime, loading, existingSchedules, allSchedules = [], onSave }) => {
  const [ranges, setRanges] = useState<Array<{ startTime: string; endTime: string; scheduleType: 'standard' | 'reschedule_only' }>>([{ startTime, endTime, scheduleType: 'standard' }]);
  const dayNames = ['Lunes','Martes','Miercoles','Jueves','Viernes','Sabado','Domingo'];
  const toMondayIdx = (jsDay: number): number => (jsDay + 6) % 7; // 0=Mon .. 6=Sun
  const initialWeekdayIdx = (() => {
    try {
      const [y,m,d] = date.split('-').map(Number);
      const jsDay = new Date(y, (m || 1) - 1, d || 1).getDay();
      return toMondayIdx(jsDay);
    } catch { return 0; }
  })();
  const [weekdayIdx, setWeekdayIdx] = useState<number>(initialWeekdayIdx);

  // Reset when modal props change (open/close cycle)
  useEffect(() => {
    const [y,m,d] = date.split('-').map(Number);
    const jsDay = new Date(y, (m || 1) - 1, d || 1).getDay();
    const newWeekdayIdx = toMondayIdx(jsDay);
    setWeekdayIdx(newWeekdayIdx);

    // Durante onboarding, auto-completar con primer horario realmente disponible
    const isOnboarding = (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay === true;

    if (isOnboarding && !startTime) {
      // Calcular schedules para el día seleccionado
      const schedulesForDay = allSchedules.length > 0
        ? allSchedules.filter(s => s.weekday === newWeekdayIdx)
        : (existingSchedules || []);

      // Obtener intervalos ocupados (no hay otros rangos aún, es el primero)
      const occupied = getOccupiedIntervals(0, [], schedulesForDay);

      // Obtener primera opción de inicio realmente disponible
      const availableStarts = getFilteredStartOptions(occupied);
      const autoStartTime = availableStarts[0] || START_TIMES_30[0];

      // Obtener primera opción de fin disponible (mínimo 60 min después)
      const availableEnds = getFilteredEndOptions(autoStartTime, occupied);
      const autoEndTime = availableEnds[0] || '';

      setRanges([{ startTime: autoStartTime, endTime: autoEndTime, scheduleType: 'standard' }]);
    } else {
      setRanges([{ startTime, endTime, scheduleType: 'standard' }]);
    }
  }, [startTime, endTime, date, allSchedules, existingSchedules]);

  const schedulesForDay = useMemo(() =>
    allSchedules.length > 0
      ? allSchedules.filter(s => s.weekday === weekdayIdx)
      : (existingSchedules || []),
    [allSchedules, existingSchedules, weekdayIdx]
  );

  const errors = useMemo(() =>
    ranges.map((r, i) => validateRange(r, i, ranges, schedulesForDay)),
    [ranges, schedulesForDay]
  );

  const updateRange = (index: number, field: 'startTime' | 'endTime', value: string) => {
    setRanges(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      // Auto-adjust endTime if it's no longer valid for the new startTime
      if (field === 'startTime') {
        const occupied = getOccupiedIntervals(index, next, schedulesForDay);
        const opts = getFilteredEndOptions(value, occupied);
        if (opts.length > 0 && !opts.includes(next[index].endTime)) {
          next[index].endTime = opts[0];
        }
      }
      return next;
    });
  };

  const removeRange = (index: number) => {
    setRanges(prev => prev.filter((_, i) => i !== index));
  };

  const addRange = () => {
    setRanges(prev => {
      const newIndex = prev.length;
      const occupied = getOccupiedIntervals(newIndex, prev, schedulesForDay);
      const availableStarts = getFilteredStartOptions(occupied);
      const defaultStart = availableStarts[0] || START_TIMES_30[0];
      const endOpts = getFilteredEndOptions(defaultStart, occupied);
      const defaultEnd = endOpts[0] || '';
      return [...prev, { startTime: defaultStart, endTime: defaultEnd, scheduleType: 'standard' as const }];
    });
  };

  if (!open) return null;

  const allFilled = ranges.every(r => r.startTime && r.endTime);
  const noErrors = errors.every(e => e === '');
  const canSave = ranges.length > 0 && allFilled && noErrors && !loading;

  return (
    <div
      id="create-work-range-modal"
      className="modal-overlay"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'ltr', zIndex: 1000 }}
      onClick={() => {
        try {
          const lock = (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay === true;
          if (!lock) onClose();
        } catch { onClose(); }
      }}
    >
      <div id="create-work-range-modal-content" className="modal-content" style={{ background: '#fff', borderRadius: '12px', width: '32rem', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div style={{ padding: '3rem', display: 'grid', placeItems: 'center' }}>
            <CircularProgress sizeRem={2.5} label="Guardando" />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.5rem 1.5rem 1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem !important', fontWeight: 700, textAlign: 'left', color: '#111827' }}>Crear Rango de Trabajo</h3>
              <button
                style={{ background: 'transparent', border: 0, fontSize: '1.5rem !important', cursor: 'pointer', color: '#6b7280', padding: 0, width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'background 0.2s' }}
                onClick={() => {
                  const isTourActive = (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay === true;
                  if (isTourActive) {
                    // Durante onboarding, simular clic en botón "Atrás" del tour
                    try {
                      const backButton = document.querySelector('.react-joyride__tooltip button') as HTMLElement | null;
                      if (backButton && backButton.textContent?.includes('Atrás')) {
                        backButton.click();
                      }
                    } catch { /* noop */ }
                  } else {
                    onClose();
                  }
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >×</button>
            </div>
            <div style={{ padding: '1.5rem', fontSize: '0.875rem !important', textAlign: 'left', overflowY: 'auto', flex: 1 }}>
              {/* Modo de asignación: solo "Día de semana", alineado a la izquierda */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '1.5rem', marginBottom: '1rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, color: '#374151' }}>
                  <input
                    type="radio"
                    name="assign-mode"
                    checked
                    readOnly
                    style={{ accentColor: '#6D57BC' }}
                  />
                  Día de semana
                </label>
              </div>
              {/* Selector de día */}
              <div style={{ marginBottom: '1rem' }} id="cwr-day-selector">
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Día</label>
                <select
                  value={weekdayIdx}
                  onChange={(e) => {
                    setWeekdayIdx(parseInt(e.target.value, 10));
                    setRanges([{ startTime: START_TIMES_30[0], endTime: '', scheduleType: 'standard' }]);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    fontSize: '0.875rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    background: '#fff',
                    color: '#111827',
                    outline: 'none'
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#6D57BC')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = '#d1d5db')}
                >
                  {dayNames.map((name, idx) => (
                    <option key={name} value={idx}>{name}</option>
                  ))}
                </select>
              </div>
              {/* Time range rows */}
              {ranges.map((range, idx) => {
                const occupied = getOccupiedIntervals(idx, ranges, schedulesForDay);
                const startOpts = getFilteredStartOptions(occupied, range.endTime);
                const endOpts = getFilteredEndOptions(range.startTime, occupied);
                const err = errors[idx];
                return (
                  <div key={idx} style={{ marginBottom: '0.75rem' }}>
                    {idx > 0 && (
                      <div style={{ borderTop: '1px solid #f3f4f6', marginBottom: '0.75rem' }} />
                    )}
                    <div id={idx === 0 ? 'cwr-time-range' : undefined} style={{ display: 'grid', gridTemplateColumns: ranges.length > 1 ? '1fr 1fr 1fr auto' : '1fr 1fr 1fr', gap: '0.75rem', alignItems: 'end' }}>
                      <div>
                        {idx === 0 && (
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Hora de inicio</label>
                        )}
                        <TimeSelect
                          value={range.startTime}
                          onChange={(v) => updateRange(idx, 'startTime', v)}
                          ariaLabel={`Hora de inicio rango ${idx + 1}`}
                          options={startOpts}
                        />
                      </div>
                      <div>
                        {idx === 0 && (
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Hora de fin</label>
                        )}
                        <TimeSelect
                          value={range.endTime}
                          onChange={(v) => updateRange(idx, 'endTime', v)}
                          ariaLabel={`Hora de fin rango ${idx + 1}`}
                          options={endOpts}
                        />
                      </div>
                      <div>
                        {idx === 0 && (
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Tipo</label>
                        )}
                        <select
                          value={range.scheduleType}
                          onChange={(e) => {
                            const val = e.target.value as 'standard' | 'reschedule_only';
                            setRanges(prev => {
                              const next = [...prev];
                              next[idx] = { ...next[idx], scheduleType: val };
                              return next;
                            });
                          }}
                          className={`w-full py-2.5 px-2 text-[0.8125rem] border border-gray-300 rounded-lg bg-white outline-none cursor-pointer focus:border-violet-700 ${
                            range.scheduleType === 'reschedule_only' ? 'text-amber-700' : 'text-gray-700'
                          }`}
                        >
                          <option value="standard">Estándar</option>
                          <option value="reschedule_only">Reprog.</option>
                        </select>
                      </div>
                      {ranges.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRange(idx)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2.25rem',
                            height: '2.25rem',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#ef4444',
                            fontSize: '1.1rem',
                            transition: 'background 0.2s',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          aria-label="Eliminar rango"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {err && (
                      <div style={{ padding: '0.5rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
                        {err}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Agregar otro button */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '0.25rem' }}>
                <button
                  type="button"
                  onClick={addRange}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: '#6D57BC',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f3ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  + Agregar otro horario
                </button>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', borderTop: '1px solid #e5e7eb', gap: '0.5rem' }}>
              <button
                disabled={!canSave}
                className="btn-primary"
                onClick={async () => {
                  if (canSave) {
                    await onSave(ranges.map(r => ({ startTime: r.startTime, endTime: r.endTime, weekdayIdx, scheduleType: r.scheduleType })));
                    try {
                      const isTourLocked = (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay === true;
                      const joyrideVisible = Boolean(document.querySelector('.react-joyride__tooltip'));
                      if (isTourLocked && joyrideVisible) {
                        window.dispatchEvent(new CustomEvent('psi-work-range-created'));
                      }
                    } catch { /* noop */ }
                    onClose();
                  }
                }}
              >
                Crear
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateWorkRangeModal;

